import useAppDispatch from '../../../hooks/useAppDispatch';
import useAppSelector from '../../../hooks/useAppSelector';
import type { Product } from '../../../models/Product';
import Icon from '../../icon/Icon';
import type { IconId } from '../../icon/icons';
import './ProductMiniature.less';
import ProductMiniatureButtons, {
    type ProductMiniatureButtonsAction
} from './ProductMiniatureButtons';
import emptyImage from '../../../img/empty-image.svg';
import IconButton from '../../button/IconButton';
import { getConnector } from '../../../services/Connector';

export type ProductMiniatureProps = {
    size: 'x-small' | 'small' | 'medium' | 'large' | 'x-large' | 'xx-large';
    aspectRatio?: '1';
    border?: boolean;
    animationDelay?: number;
    iconId?: IconId;
    product?: Product;
    clickable?: boolean;
    visibleActions?: ProductMiniatureButtonsAction[];
    onRemove?: () => void;
};

const sizes: { [size in ProductMiniatureProps['size']]: number } = {
    'x-small': 32,
    small: 48,
    medium: 80,
    large: 120,
    'x-large': 180,
    'xx-large': 300
};

const ProductMiniature = ({
    size,
    aspectRatio: aspectRatioProp,
    animationDelay = 0,
    border,
    iconId,
    product,
    clickable = false,
    visibleActions,
    onRemove
}: ProductMiniatureProps) => {
    const dispatch = useAppDispatch();
    const aspectRatio =
        aspectRatioProp ?? useAppSelector(state => state.uiState.aspectRatio);
    const productImageUrl = product?.images[0]?.url
        ? getConnector().getScaledImage(product.images[0].url, sizes[size])
        : emptyImage;
    return (
        <div
            className={`bri-ProductMiniature bri-ZoomIn bri-ProductMiniature-size-${size} ${border ? 'bri-border' : ''} ${
                iconId ? ' bri-icon' : ''
            } ${clickable ? 'bri-clickable' : ''}`}
            style={{
                animationDelay: `${animationDelay}ms`,
                '--product-miniature-size': `${sizes[size]}px`
            }}
            onClick={() =>
                clickable &&
                product &&
                dispatch.featureState.startFeature({
                    featureType: 'product-detail',
                    productId: product.productId,
                    product,
                    source: 'brilliant'
                })
            }
        >
            <div
                className={`bri-ProductMiniature-image`}
                style={{ aspectRatio }}
            >
                <img
                    src={productImageUrl}
                    alt={product?.images[0].altText}
                    style={{ aspectRatio }}
                />
            </div>
            {iconId && (
                <Icon
                    iconId={iconId}
                    size={size === 'small' ? 'small' : 'medium'}
                />
            )}
            {product && (
                <ProductMiniatureButtons
                    product={product}
                    visibleActions={visibleActions}
                />
            )}
            {onRemove && (
                <div className="bri-ProductMiniature-remove-button-container">
                    <IconButton
                        className="bri-ProductMiniature-remove-button"
                        variant="light"
                        iconId="close"
                        size="x-small"
                        onClick={event => {
                            event.stopPropagation();
                            onRemove();
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default ProductMiniature;
